import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useAgents } from '@/contexts/AgentsContext'
import type { ActionCardData } from '@/lib/chatTypes'
import { MessageList } from '@/components/agents/MessageList'
import { SuggestionPills } from '@/components/agents/SuggestionPills'
import { ChatInput } from '@/components/agents/ChatInput'
import { useActionConfirmation } from '@/hooks/useActionConfirmation'

export type ChatContainerProps = {
  welcome?: ReactNode
  className?: string
}

// Liste + pills + saisie, SANS historique : réutilisé par SplitView (6.3) et
// ChatPanel (9.1) avec le même AgentsProvider.
export function ChatContainer({ welcome, className }: ChatContainerProps) {
  const {
    messages,
    messagesLoading,
    messagesError,
    suggestions,
    isStreaming,
    sendMessage,
    stop,
    setActionCardState,
    activeConversationId,
    navigateTo,
  } = useAgents()
  const [input, setInput] = useState('')

  // Règle unique : aucun envoi (saisie, pill, CTA) pendant un stream. Pour
  // interrompre la réponse, l'utilisateur passe explicitement par « Stop ».
  const handleSend = useCallback(
    (text: string) => {
      if (isStreaming) return
      setInput('')
      void sendMessage(text)
    },
    [isStreaming, sendMessage]
  )

  // Story 7.1 — confirmation réelle : « Confirmer » ouvre une fenêtre
  // d'annulation de 5 s puis appelle l'endpoint de confirmation ; « Annuler »
  // annule l'action (ou interrompt la fenêtre en cours, même pendant un stream).
  const { confirm, cancel } = useActionConfirmation({
    conversationId: activeConversationId,
    setActionCardState,
  })

  const handleActionPrimary = useCallback(
    (messageId: string, card: ActionCardData) => {
      if (isStreaming) return
      confirm(messageId, card)
    },
    [isStreaming, confirm]
  )

  const handleActionSecondary = useCallback(
    (messageId: string, card: ActionCardData) => {
      if (isStreaming && card.state !== 'confirmed') return
      cancel(messageId, card)
    },
    [isStreaming, cancel]
  )

  const pillsHidden = isStreaming || input.trim() !== '' || messagesLoading || Boolean(messagesError)

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <MessageList
        messages={messages}
        welcome={welcome}
        loading={messagesLoading}
        error={messagesError}
        onActionPrimary={handleActionPrimary}
        onActionSecondary={handleActionSecondary}
        actionsDisabled={isStreaming}
        onNavigate={navigateTo}
      />
      <div className="mx-auto w-full max-w-3xl space-y-3 px-4 pb-4 pt-2">
        <SuggestionPills suggestions={suggestions} onSelect={handleSend} hidden={pillsHidden} />
        <ChatInput
          value={input}
          onValueChange={setInput}
          onSend={handleSend}
          onStop={stop}
          isStreaming={isStreaming}
        />
      </div>
    </div>
  )
}
